import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaAngleDown, FaAngleRight } from 'react-icons/fa';
import styles from './ReservationHistoryTab.module.scss';
import AvatarWithText from 'common/components/avatar/AvatarWithText';
import Badge from 'common/components/badge/Badge';
import DateFormat from 'common/components/dateFormat/dateFormat';
import { DATE_FORMAT_DEFAUT, TIME_FORMAT_DEFAULT, LOGGER_LOG_TYPE } from 'Config';
import VehicleReservationsService from 'api/vehicleReservations/VehicleReservationsService';
import { VehicleReservationHistoryDto, HistoryChanges } from 'api/vehicleReservations/models/VehicleReservationHistoryDto';
import Logger from 'common/services/Logger';
import moment from 'moment';
import { getReservationStatusBadgePreset, getReservationStatusLabel } from 'common/utils/reservationStatus';

interface ReservationHistoryTabProps {
    reservationId: string;
    refreshKey?: number;
}

const ReservationHistoryTab: React.FC<ReservationHistoryTabProps> = ({ reservationId, refreshKey }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [history, setHistory] = useState<VehicleReservationHistoryDto[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);

    useEffect(() => {
        if (isOpen && !hasLoaded) {
            loadHistory();
        }
    }, [isOpen, hasLoaded]);

    // Reload history after edits (refreshKey changes)
    useEffect(() => {
        if (hasLoaded && refreshKey) {
            setHasLoaded(false);
        }
    }, [refreshKey]);

    const loadHistory = async () => {
        try {
            setIsLoading(true);
            const data = await VehicleReservationsService.getHistory(reservationId);
            setHistory(data);
            setHasLoaded(true);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't load reservation history`, error);
        } finally {
            setIsLoading(false);
        }
    };

    const getActionLabel = (action: string): string => {
        switch (action) {
            case 'CREATE':
                return t('reservations.history.action_create') as string;
            case 'EDIT':
                return t('reservations.history.action_edit') as string;
            case 'APPROVE':
                return t('reservations.history.action_approve') as string;
            case 'REJECT':
                return t('reservations.history.action_reject') as string;
            case 'CANCEL_APPROVAL':
                return t('reservations.history.action_cancel_approval') as string;
            default:
                return action;
        }
    };

    const getStatusFromAction = (action: string, changes: HistoryChanges): string | null => {
        if (changes.Status?.after) {
            return changes.Status.after;
        }
        switch (action) {
            case 'CREATE':
                return 'PENDING_APPROVAL';
            case 'APPROVE':
                return 'APPROVED';
            case 'REJECT':
                return 'REJECTED';
            case 'CANCEL_APPROVAL':
                return 'PENDING_APPROVAL';
            default:
                return null;
        }
    };

    const formatDateValue = (value: any): string => {
        if (!value) return '-';
        return moment(value).format(`${DATE_FORMAT_DEFAUT} | ${TIME_FORMAT_DEFAULT}`);
    };

    const renderChanges = (changes: HistoryChanges, action: string): React.ReactNode => {
        const fieldChanges: React.ReactNode[] = [];
        const vehicleElements: React.ReactNode[] = [];

        // Show field changes (dates, reason) for EDIT and APPROVE actions
        if (action === 'EDIT' || action === 'APPROVE') {
            if (changes.StartDate) {
                fieldChanges.push(
                    <div key="startDate" className={styles.changeItem}>
                        <span className={styles.changeLabel}>{t('reservations.history.change_start_date')}:</span>
                        <span>{formatDateValue(changes.StartDate.after)}</span>
                    </div>
                );
            }
            if (changes.EndDate) {
                fieldChanges.push(
                    <div key="endDate" className={styles.changeItem}>
                        <span className={styles.changeLabel}>{t('reservations.history.change_end_date')}:</span>
                        <span>{formatDateValue(changes.EndDate.after)}</span>
                    </div>
                );
            }
            if (changes.Reason) {
                fieldChanges.push(
                    <div key="reason" className={styles.changeItem}>
                        <span className={styles.changeLabel}>{t('reservations.history.change_reason')}:</span>
                        <span>{changes.Reason.after || '-'}</span>
                    </div>
                );
            }
            if (changes.LocalName) {
                fieldChanges.push(
                    <div key="local" className={styles.changeItem}>
                        <span className={styles.changeLabel}>{t('reservations.history.change_local')}:</span>
                        <span>{changes.LocalName.after || '-'}</span>
                    </div>
                );
            }
            if (changes.DriverName) {
                fieldChanges.push(
                    <div key="driver" className={styles.changeItem}>
                        <span className={styles.changeLabel}>{t('reservations.history.change_driver')}:</span>
                        <span>{changes.DriverName.after || '-'}</span>
                    </div>
                );
            }
        }

        // Show requested vehicle details when it differs from assigned (APPROVE/EDIT/CREATE)
        const requestedVehicleElements: React.ReactNode[] = [];
        if (changes.RequestedVehicleRegistrationNumber?.after) {
            requestedVehicleElements.push(
                <div key="reqVehicleReg" className={styles.changeLabel}>
                    {changes.RequestedVehicleRegistrationNumber.after}
                </div>
            );
            const reqBrandName = changes.RequestedVehicleBrandName?.after;
            const reqModelName = changes.RequestedVehicleModelName?.after;
            if (reqBrandName || reqModelName) {
                requestedVehicleElements.push(
                    <div key="reqVehicleBrandModel">
                        {reqBrandName}{reqBrandName && reqModelName ? ' | ' : ''}{reqModelName}
                    </div>
                );
            }
        }

        // Show assigned vehicle details for APPROVE/EDIT actions with vehicle change
        if ((action === 'APPROVE' || action === 'EDIT') && changes.VehicleId?.after) {
            if (changes.VehicleRegistrationNumber?.after) {
                vehicleElements.push(
                    <div key="vehicleReg" className={styles.changeLabel}>
                        {changes.VehicleRegistrationNumber.after}
                    </div>
                );
            }
            const brandName = changes.VehicleBrandName?.after;
            const modelName = changes.VehicleModelName?.after;
            if (brandName || modelName) {
                vehicleElements.push(
                    <div key="vehicleBrandModel">
                        {brandName}{brandName && modelName ? ' | ' : ''}{modelName}
                    </div>
                );
            }
        }

        // For REJECT action, show rejection reason
        if (action === 'REJECT' && changes.RejectionReason?.after) {
            fieldChanges.push(
                <div key="rejection" className={styles.changeItem}>
                    <span className={styles.changeLabel}>{t('reservations.history.rejection_reason')}:</span>
                    <span>{changes.RejectionReason.after}</span>
                </div>
            );
        }

        if (fieldChanges.length === 0 && vehicleElements.length === 0 && requestedVehicleElements.length === 0) return null;

        return (
            <div className={styles.changes}>
                {fieldChanges.length > 0 && (
                    <div className={styles.changesSection}>
                        <div className={styles.changesSectionTitle}>{t('reservations.history.changes')}:</div>
                        {fieldChanges}
                    </div>
                )}
                {requestedVehicleElements.length > 0 && (
                    <div className={styles.changesSection}>
                        <div className={styles.changesSectionTitle}>{t('reservations.history.asset_requested')}:</div>
                        {requestedVehicleElements}
                    </div>
                )}
                {vehicleElements.length > 0 && (
                    <div className={styles.changesSection}>
                        <div className={styles.changesSectionTitle}>{t('reservations.history.asset_assigned')}:</div>
                        {vehicleElements}
                    </div>
                )}
            </div>
        );
    };

    const renderHistoryItem = (item: VehicleReservationHistoryDto, index: number) => {
        let changes: HistoryChanges = {};
        try {
            changes = JSON.parse(item.changes || '{}');
        } catch {
            changes = {};
        }

        const status = getStatusFromAction(item.action, changes);

        return (
            <div key={item.id || index} className={styles.historyItem}>
                <div className={styles.timelineDot} />
                {index < history.length - 1 && <div className={styles.timelineLine} />}

                <div className={styles.historyItemContent}>
                    <div className={styles.historyHeader}>
                        <span className={styles.actionLabel}>{getActionLabel(item.action)}</span>
                        <span className={styles.historyDate}>
                            <DateFormat value={item.createdDate} format={DATE_FORMAT_DEFAUT} />
                            {' | '}
                            {moment(item.createdDate).format(TIME_FORMAT_DEFAULT)}
                        </span>
                        {status && (
                            <Badge
                                text={getReservationStatusLabel(status, t)}
                                preset={getReservationStatusBadgePreset(status)}
                            />
                        )}
                    </div>

                    <div className={styles.historyUser}>
                        <AvatarWithText src={item.userPhotoUrl} size="x2">
                            <span className={styles.userName}>{item.userName || '-'}</span>
                        </AvatarWithText>
                    </div>

                    {renderChanges(changes, item.action)}
                </div>
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.historyBar} onClick={() => setIsOpen(!isOpen)}>
                <span className={styles.toggleIcon}>{isOpen ? <FaAngleDown /> : <FaAngleRight />}</span>
                <span>{t('reservations.history.title')}</span>
            </div>

            {isOpen && (
                <div className={styles.historyContent}>
                    {isLoading && (
                        <div className={styles.loading}>{t('common.loading')}...</div>
                    )}
                    {!isLoading && history.length === 0 && (
                        <div className={styles.noHistory}>{t('reservations.history.no_history')}</div>
                    )}
                    {!isLoading && history.length > 0 && (
                        <div className={styles.timeline}>
                            {history.map((item, index) => renderHistoryItem(item, index))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ReservationHistoryTab;
